/**
 * "Export…" for one analysis: choose its reports, the parts of each (Result,
 * Exceptions, Supporting rows, Summary) and, if wanted, which columns, then
 * download a workbook holding only that.
 *
 * What is offered comes from real data: Exceptions and Supporting rows only
 * when the report has them, and the column lists from the report's own result.
 */

import { useEffect, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useMutation } from '@tanstack/react-query';
import { ErrorNotice } from '../../shared/ui';
import Loader from '../../shared/components/Loader';
import { useToast } from '../../provider/toast';
import {
	exportClientAnalysisSelection,
	useAnalysisResultQuery,
	type AnalysisExportPart,
	type AnalysisExportSelection,
} from '../../queries/analysis';
import type { AnalysisReportRow, AnalysisRow } from './analysisRows';

const ROW_ID = 'result_row_id';

const PART_LABEL: Record<AnalysisExportPart, string> = {
	result: 'Result',
	exceptions: 'Exceptions',
	supporting: 'Supporting rows',
	summary: 'Summary',
};

/** The parts this report really has (the summary is always offered: the
 *  overview cannot tell whether one exists, so the server writes it if so). */
function offeredParts(report: AnalysisReportRow): AnalysisExportPart[] {
	const parts: AnalysisExportPart[] = ['result'];
	if (report.row.exceptionFindings !== null) parts.push('exceptions');
	if (report.row.supportingRows !== null) parts.push('supporting');
	parts.push('summary');
	return parts;
}

interface ReportChoice {
	on: boolean;
	parts: AnalysisExportPart[];
	/** Column keys chosen for Result & Exceptions; null = every column. */
	resultColumns: string[] | null;
	/** Column keys chosen for Supporting rows; null = every column. */
	supportingColumns: string[] | null;
	/** How many columns each list was narrowed from, for the closed-picker hint. */
	resultTotal: number;
	supportingTotal: number;
}

type Choices = Record<number, ReportChoice>;

function initialChoices(entry: AnalysisRow): Choices {
	const choices: Choices = {};
	for (const report of entry.reports) {
		choices[report.index] = {
			on: report.row.status === 'COMPLETED',
			parts: offeredParts(report),
			resultColumns: null,
			supportingColumns: null,
			resultTotal: 0,
			supportingTotal: 0,
		};
	}
	return choices;
}

const usesResultParts = (choice: ReportChoice) =>
	choice.parts.includes('result') || choice.parts.includes('exceptions');

const hasColumns = (columns: string[]) => columns.some((key) => key !== ROW_ID);

/** Result columns named differently from the raw field they show. The same
 *  table as `_RESULT_FIELD` in python/app/application/analysis_store.py —
 *  keep the two in step. */
const RESULT_FIELD: Record<string, string> = {
	material: 'material_desc',
	customer: 'customer_id',
	invoice: 'invoice_number',
	invoice_type: 'document_type',
	description: 'document_type_desc',
	taxable: 'taxable_value',
};

/**
 * The Supporting rows columns that show a kept Result column's own field (the
 * same key, or its alias above). The server always exports them beside the
 * Result columns (Python's `_linked_supporting`), so the dialog shows them
 * locked rather than letting a tick suggest otherwise.
 */
function linkedSupporting(
	resultKeys: string[],
	supportingKeys: string[],
): string[] {
	const available = new Set(supportingKeys);
	const linked: string[] = [];
	for (const key of resultKeys) {
		const match = [key, RESULT_FIELD[key]].find(
			(candidate) => candidate !== undefined && available.has(candidate),
		);
		if (match && match !== ROW_ID && !linked.includes(match))
			linked.push(match);
	}
	return linked;
}

/**
 * The sheets the server will really write for one report, by the same rules
 * as Python's `analysis_workbook`: Exceptions that repeat the Result (same row
 * count) are not a separate sheet while Result is chosen, and Supporting rows
 * only exist when the report has them. Whether a report has a Summary sheet
 * is not in the overview, so it is reported apart, as "maybe".
 */
function sheetsFor(
	report: AnalysisReportRow,
	parts: AnalysisExportPart[],
): { certain: number; maybeSummary: boolean } {
	const { resultRows, exceptionFindings, supportingRows } = report.row;
	let certain = 0;
	if (parts.includes('result')) certain += 1;
	if (parts.includes('supporting') && supportingRows !== null) certain += 1;
	if (
		parts.includes('exceptions') &&
		exceptionFindings !== null &&
		(exceptionFindings !== resultRows || !parts.includes('result'))
	)
		certain += 1;
	return { certain, maybeSummary: parts.includes('summary') };
}

/** The request to send, or the reason there is nothing valid to send yet. */
function buildSelection(
	entry: AnalysisRow,
	choices: Choices,
): { selection: AnalysisExportSelection } | { problem: string } {
	const reports: AnalysisExportSelection['reports'] = [];
	for (const report of entry.reports) {
		const choice = choices[report.index];
		if (!choice?.on) continue;
		const name =
			entry.reports.length > 1
				? `Report ${report.index}`
				: 'The analysis';
		if (choice.parts.length === 0)
			return { problem: `${name}: choose at least one part.` };
		const columns: { result?: string[]; supporting?: string[] } = {};
		const usesResult =
			choice.parts.includes('result') ||
			choice.parts.includes('exceptions');
		if (usesResult && choice.resultColumns) {
			if (!hasColumns(choice.resultColumns))
				return {
					problem: `${name}: choose at least one Result column.`,
				};
			columns.result = choice.resultColumns.filter(
				(key) => key !== ROW_ID,
			);
		}
		if (choice.parts.includes('supporting') && choice.supportingColumns) {
			if (!hasColumns(choice.supportingColumns))
				return {
					problem: `${name}: choose at least one Supporting rows column.`,
				};
			columns.supporting = choice.supportingColumns.filter(
				(key) => key !== ROW_ID,
			);
		}
		reports.push({
			index: report.index,
			parts: choice.parts,
			...(columns.result || columns.supporting ? { columns } : {}),
		});
	}
	if (reports.length === 0) return { problem: 'Choose at least one report.' };
	return { selection: { reports } };
}

type ColumnGroup = 'result' | 'supporting';

const GROUP_LABEL: Record<ColumnGroup, string> = {
	result: 'Result & Exceptions',
	supporting: 'Supporting rows',
};

/** A pill that switches something on or off: filled with a tick when on. */
function ToggleChip({
	label,
	on,
	onToggle,
	disabled,
	icon,
}: {
	label: string;
	on: boolean;
	onToggle?: () => void;
	disabled?: boolean;
	icon?: React.ReactElement;
}) {
	return (
		<Chip
			clickable={!disabled}
			size="small"
			label={label}
			disabled={disabled}
			aria-pressed={on}
			icon={icon ?? (on ? <CheckIcon /> : undefined)}
			variant={on ? 'filled' : 'outlined'}
			onClick={onToggle}
			sx={{
				fontSize: 'var(--fs-body)',
				borderColor: on ? 'var(--accent-border)' : 'var(--line-strong)',
				bgcolor: on ? 'var(--accent-surface)' : 'transparent',
				color: on ? 'var(--accent)' : 'var(--ink-2)',
				'&:hover': {
					bgcolor: on
						? 'var(--accent-surface)'
						: 'var(--surface-sunken)',
				},
				'& .MuiChip-icon': { fontSize: 14, color: 'inherit' },
			}}
		/>
	);
}

const sectionLabel = {
	fontSize: 'var(--fs-small)',
	fontWeight: 600,
	color: 'var(--ink-2)',
} as const;

const compactTabs = {
	minHeight: 28,
	'& .MuiTab-root': {
		minHeight: 28,
		py: 0,
		px: 'var(--s2)',
		fontSize: 'var(--fs-small)',
		textTransform: 'none',
	},
} as const;

/** Column chips for one part, read from the report's own result. */
function ColumnPicker({
	auditId,
	slug,
	part,
	chosen,
	knownTotal,
	linkedResult,
	onChange,
}: {
	auditId: string;
	slug: string;
	part: ColumnGroup;
	chosen: string[] | null;
	knownTotal: number;
	/** Supporting rows only: the Result columns being exported. Their own
	 *  fields are locked on (see `linkedSupporting`). */
	linkedResult?: string[] | null;
	onChange: (next: string[] | null, total: number) => void;
}) {
	const [search, setSearch] = useState('');
	const query = useAnalysisResultQuery(
		auditId,
		slug,
		{ part, page: 1, pageSize: 1 },
		true,
	);
	const columns = query.data?.columns ?? [];
	const all = columns.map((column) => column.key);
	const locked =
		part === 'supporting' && linkedResult
			? linkedSupporting(linkedResult, all)
			: [];
	/* What will be exported: the ticks, plus every locked column. */
	const selected = chosen
		? all.filter((key) => chosen.includes(key) || locked.includes(key))
		: all;

	/* Tell the footer how many columns there are, once they are known, and keep
     a narrowed list holding every locked column. */
	useEffect(() => {
		if (all.length > 0 && all.length !== knownTotal)
			onChange(chosen, all.length);
		else if (chosen && locked.some((key) => !chosen.includes(key)))
			onChange(selected, all.length);
	});

	const toggle = (key: string) => {
		if (locked.includes(key)) return;
		const next = selected.includes(key)
			? selected.filter((candidate) => candidate !== key)
			: all.filter(
					(candidate) =>
						candidate === key || selected.includes(candidate),
				);
		onChange(next.length === all.length ? null : next, all.length);
	};

	const count = all.filter(
		(key) => key === ROW_ID || selected.includes(key),
	).length;
	const needle = search.trim().toLowerCase();
	const shown = columns.filter(
		(column) =>
			column.key === ROW_ID ||
			!needle ||
			column.label.toLowerCase().includes(needle),
	);

	return (
		<Box display="flex" flexDirection="column" gap="var(--s2)">
			<Box display="flex" alignItems="center" gap="var(--s2)">
				<TextField
					size="small"
					placeholder="Find a column"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					sx={{
						width: 200,
						'& input': { fontSize: 'var(--fs-body)' },
					}}
					slotProps={{ htmlInput: { 'aria-label': 'Find a column' } }}
				/>
				<Typography
					sx={{
						fontSize: 'var(--fs-small)',
						color: 'var(--ink-3)',
						flex: 1,
					}}
				>
					{all.length > 0 ? `${count} of ${all.length} selected` : ''}
				</Typography>
				<Button
					size="small"
					disabled={all.length === 0}
					onClick={() => onChange(null, all.length)}
				>
					All
				</Button>
				<Button
					size="small"
					disabled={all.length === 0}
					onClick={() =>
						onChange(
							all.filter(
								(key) => key === ROW_ID || locked.includes(key),
							),
							all.length,
						)
					}
				>
					None
				</Button>
			</Box>
			<Box
				sx={{
					maxHeight: 220,
					minHeight: 64,
					overflowY: 'auto',
					border: '1px solid var(--line)',
					borderRadius: 'var(--r-lg)',
					p: 'var(--s2)',
				}}
			>
				{query.isPending && <Loader />}
				{query.error && (
					<>
						<ErrorNotice
							error={query.error}
							onRetry={() => void query.refetch()}
						/>
						<Typography
							sx={{
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
							}}
						>
							All columns will be exported.
						</Typography>
					</>
				)}
				{columns.length > 0 && (
					<Box display="flex" flexWrap="wrap" gap="var(--s1)">
						{shown.map((column) =>
							column.key === ROW_ID ? (
								<Tooltip
									key={column.key}
									title="Always included — it links each result row to its supporting rows."
								>
									<span>
										<ToggleChip
											label={column.label}
											on
											disabled
											icon={<LockOutlinedIcon />}
										/>
									</span>
								</Tooltip>
							) : locked.includes(column.key) ? (
								<Tooltip
									key={column.key}
									title="Included because this column is on the Result sheet you are exporting."
								>
									<span>
										<ToggleChip
											label={column.label}
											on
											disabled
											icon={<LockOutlinedIcon />}
										/>
									</span>
								</Tooltip>
							) : (
								<ToggleChip
									key={column.key}
									label={column.label}
									on={selected.includes(column.key)}
									onToggle={() => toggle(column.key)}
								/>
							),
						)}
						{shown.length === 0 && (
							<Typography
								sx={{
									fontSize: 'var(--fs-small)',
									color: 'var(--ink-3)',
								}}
							>
								No column matches.
							</Typography>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
}

function ExportForm({
	auditId,
	entry,
	onClose,
}: {
	auditId: string;
	entry: AnalysisRow;
	onClose: () => void;
}) {
	const toast = useToast();
	const [choices, setChoices] = useState<Choices>(() =>
		initialChoices(entry),
	);
	const [activeReport, setActiveReport] = useState<number | null>(null);
	const [activeGroup, setActiveGroup] = useState<ColumnGroup>('result');
	const multi = entry.reports.length > 1;
	const built = buildSelection(entry, choices);
	const problem = 'problem' in built ? built.problem : null;

	const exportMutation = useMutation({
		mutationFn: (selection: AnalysisExportSelection) =>
			exportClientAnalysisSelection(auditId, entry.number, selection),
		onSuccess: () => {
			toast('ok', 'Your selected export is downloading.');
			onClose();
		},
	});

	const update = (index: number, patch: Partial<ReportChoice>) =>
		setChoices((current) => ({
			...current,
			[index]: { ...current[index]!, ...patch },
		}));

	const chosenReports = entry.reports.filter(
		(report) =>
			choices[report.index]!.on && report.row.status === 'COMPLETED',
	);
	const editing =
		chosenReports.find((report) => report.index === activeReport) ??
		chosenReports[0];
	const choice = editing ? choices[editing.index]! : null;
	const offered = editing ? offeredParts(editing) : [];
	const groups: ColumnGroup[] = [];
	if (choice && usesResultParts(choice)) groups.push('result');
	if (choice?.parts.includes('supporting')) groups.push('supporting');
	const group = groups.includes(activeGroup) ? activeGroup : groups[0];

	/* Supporting rows keep the fields of the Result columns being exported:
     the narrowed Result list, or every Result column (read through the same
     cached query the Result tab uses). */
	const linksResult = Boolean(
		choice &&
		usesResultParts(choice) &&
		choice.parts.includes('supporting'),
	);
	const resultColumnsQuery = useAnalysisResultQuery(
		auditId,
		editing?.row.slug ?? '',
		{ part: 'result', page: 1, pageSize: 1 },
		linksResult && Boolean(editing),
	);
	const linkedResult = linksResult
		? (choice?.resultColumns ??
			resultColumnsQuery.data?.columns.map((column) => column.key) ??
			null)
		: null;

	/* The footer summary: sheets and (where known) columns across every report. */
	let sheets = 0;
	let maybeSummary = false;
	let columnsChosen = 0;
	let columnsTotal = 0;
	for (const report of chosenReports) {
		const each = choices[report.index]!;
		const written = sheetsFor(report, each.parts);
		sheets += written.certain;
		maybeSummary ||= written.maybeSummary;
		if (usesResultParts(each) && each.resultTotal > 0) {
			columnsTotal += each.resultTotal;
			columnsChosen += each.resultColumns?.length ?? each.resultTotal;
		}
		if (each.parts.includes('supporting') && each.supportingTotal > 0) {
			columnsTotal += each.supportingTotal;
			columnsChosen +=
				each.supportingColumns?.length ?? each.supportingTotal;
		}
	}
	const summary =
		`${sheets} ${sheets === 1 ? 'sheet' : 'sheets'}` +
		(maybeSummary ? ' + Summary where the report has one' : '') +
		(columnsTotal > 0
			? ` · ${columnsChosen} of ${columnsTotal} columns`
			: '');

	return (
		<>
			<DialogTitle sx={{ pb: 'var(--s1)' }}>
				{`Export ${entry.number}. ${entry.title}`}
				<Typography
					sx={{
						fontSize: 'var(--fs-small)',
						color: 'var(--ink-3)',
						fontWeight: 400,
					}}
				>
					Only what you choose is exported. The About sheet records
					the selection.
				</Typography>
			</DialogTitle>
			<DialogContent>
				<Box
					display="flex"
					flexDirection="column"
					gap="var(--s3)"
					pt="var(--s1)"
				>
					{multi && (
						<Box
							display="flex"
							flexDirection="column"
							gap="var(--s1)"
						>
							<Typography sx={sectionLabel}>
								Reports
								{chosenReports.length > 1 && (
									<Box
										component="span"
										sx={{
											fontWeight: 400,
											color: 'var(--ink-3)',
											ml: 'var(--s2)',
										}}
									>
										Click a report to choose its sheets and
										columns.
									</Box>
								)}
							</Typography>
							{/* One row per report: tick to include it, click to edit its
                  sheets and columns below. Completed is the normal state, so
                  only a report that is not completed says anything. */}
							<Box
								role="list"
								sx={{
									border: '1px solid var(--line)',
									borderRadius: 'var(--r-lg)',
									overflow: 'hidden',
								}}
							>
								{entry.reports.map((report, position) => {
									const completed =
										report.row.status === 'COMPLETED';
									const on =
										choices[report.index]!.on && completed;
									const current =
										on && editing?.index === report.index;
									const select = () =>
										on && setActiveReport(report.index);
									return (
										<Box
											key={report.row.slug}
											role="listitem"
											aria-current={
												current ? 'true' : undefined
											}
											tabIndex={on ? 0 : -1}
											onClick={select}
											onKeyDown={(event) => {
												if (
													event.target !==
													event.currentTarget
												)
													return;
												if (
													event.key === 'Enter' ||
													event.key === ' '
												) {
													event.preventDefault();
													select();
												}
											}}
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 'var(--s2)',
												minHeight: 40,
												pl: 'var(--s1)',
												pr: 'var(--s3)',
												borderTop:
													position === 0
														? 'none'
														: '1px solid var(--line)',
												cursor: on
													? 'pointer'
													: 'default',
												bgcolor: current
													? 'var(--accent-surface)'
													: 'transparent',
												boxShadow: current
													? 'inset 3px 0 0 var(--accent)'
													: 'none',
												'&:hover':
													on && !current
														? {
																bgcolor:
																	'var(--surface-hover)',
															}
														: {},
												'&:focus-visible': {
													outline:
														'2px solid var(--focus)',
													outlineOffset: '-2px',
												},
											}}
										>
											<Checkbox
												size="small"
												checked={on}
												disabled={!completed}
												inputProps={{
													'aria-label': `Include report ${report.index}`,
												}}
												onClick={(event) =>
													event.stopPropagation()
												}
												onChange={() => {
													update(report.index, {
														on: !choices[
															report.index
														]!.on,
													});
													if (
														!choices[report.index]!
															.on
													)
														setActiveReport(
															report.index,
														);
												}}
											/>
											<Typography
												noWrap
												title={`${report.index}. ${report.label}`}
												sx={{
													flex: 1,
													minWidth: 0,
													fontSize: 'var(--fs-body)',
													color: completed
														? 'var(--ink)'
														: 'var(--ink-3)',
												}}
											>
												<Box
													component="span"
													sx={{ fontWeight: 600 }}
												>
													Report {report.index}
												</Box>
												<Box
													component="span"
													sx={{
														color: 'var(--ink-3)',
														mx: '6px',
													}}
												>
													·
												</Box>
												{report.label}
											</Typography>
											{!completed && (
												<Typography
													component="span"
													noWrap
													sx={{
														fontSize:
															'var(--fs-small)',
														color: 'var(--ink-3)',
													}}
												>
													Not completed — run it first
												</Typography>
											)}
										</Box>
									);
								})}
							</Box>
						</Box>
					)}
					{!multi && entry.reports[0]?.row.status !== 'COMPLETED' && (
						<Typography
							sx={{
								fontSize: 'var(--fs-small)',
								color: 'var(--ink-3)',
							}}
						>
							Not completed — run it first
						</Typography>
					)}

					{editing && choice && (
						<>
							<Box
								display="flex"
								flexDirection="column"
								gap="var(--s1)"
							>
								<Typography sx={sectionLabel}>
									Sheets
								</Typography>
								<Box
									display="flex"
									flexWrap="wrap"
									gap="var(--s1)"
								>
									{offered.map((part) => {
										const chip = (
											<ToggleChip
												label={PART_LABEL[part]}
												on={choice.parts.includes(part)}
												onToggle={() =>
													update(editing.index, {
														parts: choice.parts.includes(
															part,
														)
															? choice.parts.filter(
																	(
																		candidate,
																	) =>
																		candidate !==
																		part,
																)
															: offered.filter(
																	(
																		candidate,
																	) =>
																		candidate ===
																			part ||
																		choice.parts.includes(
																			candidate,
																		),
																),
													})
												}
											/>
										);
										return part === 'summary' ? (
											<Tooltip
												key={part}
												title="Included when the report has a summary sheet"
											>
												<span>{chip}</span>
											</Tooltip>
										) : (
											<Box key={part}>{chip}</Box>
										);
									})}
								</Box>
							</Box>

							{group && (
								<Box
									display="flex"
									flexDirection="column"
									gap="var(--s1)"
								>
									<Box
										display="flex"
										alignItems="center"
										justifyContent="space-between"
									>
										<Typography sx={sectionLabel}>
											Columns
										</Typography>
										{groups.length > 1 && (
											<Tabs
												value={group}
												onChange={(
													_,
													value: ColumnGroup,
												) => setActiveGroup(value)}
												sx={compactTabs}
												aria-label="Columns of"
											>
												{groups.map((candidate) => (
													<Tab
														key={candidate}
														value={candidate}
														label={
															GROUP_LABEL[
																candidate
															]
														}
													/>
												))}
											</Tabs>
										)}
									</Box>
									<ColumnPicker
										key={`${editing.index}-${group}`}
										auditId={auditId}
										slug={editing.row.slug}
										part={group}
										chosen={
											group === 'result'
												? choice.resultColumns
												: choice.supportingColumns
										}
										knownTotal={
											group === 'result'
												? choice.resultTotal
												: choice.supportingTotal
										}
										linkedResult={
											group === 'supporting'
												? linkedResult
												: null
										}
										onChange={(next, total) =>
											update(
												editing.index,
												group === 'result'
													? {
															resultColumns: next,
															resultTotal: total,
														}
													: {
															supportingColumns:
																next,
															supportingTotal:
																total,
														},
											)
										}
									/>
								</Box>
							)}
						</>
					)}

					<ErrorNotice error={exportMutation.error} />
				</Box>
			</DialogContent>
			<DialogActions
				sx={{ justifyContent: 'space-between', px: 'var(--s5)' }}
			>
				<Typography
					sx={{
						fontSize: 'var(--fs-small)',
						color: problem ? 'var(--danger-ink)' : 'var(--ink-3)',
					}}
				>
					{problem ?? summary}
				</Typography>
				<Box display="flex" gap="var(--s1)">
					<Button onClick={onClose}>Cancel</Button>
					<Button
						variant="contained"
						loading={exportMutation.isPending}
						disabled={problem !== null || exportMutation.isPending}
						onClick={() => {
							if ('selection' in built)
								exportMutation.mutate(built.selection);
						}}
					>
						Export
					</Button>
				</Box>
			</DialogActions>
		</>
	);
}

export function ExportAnalysisDialog({
	open,
	onClose,
	auditId,
	entry,
}: {
	open: boolean;
	onClose: () => void;
	auditId: string;
	entry: AnalysisRow | null;
}) {
	/* Keep the last analysis while the dialog fades out. The form lives inside
     the dialog, so it starts afresh each time the dialog opens. */
	const last = useRef(entry);
	if (entry) last.current = entry;
	const shown = entry ?? last.current;
	return (
		<Dialog
			open={open && shown !== null}
			onClose={onClose}
			fullWidth
			maxWidth="md"
		>
			{shown && (
				<ExportForm auditId={auditId} entry={shown} onClose={onClose} />
			)}
		</Dialog>
	);
}
